import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileTextIcon, UploadIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  createJobAttachment,
  jobAttachmentsQueryKey,
  listJobAttachments,
} from '@/lib/api/jobs';
import { ACCEPTED_ATTACHMENT_TYPES } from '@/lib/constants';
import { uploadFile } from '@/lib/storage';

import { formatDate } from './helpers';

interface Props {
  businessId: string;
  jobId: string;
}

// Photo/file gallery + uploader for the job detail modal. The upload runs
// inside the mutation's `mutationFn` (presigned-URL upload → then the
// attachments endpoint), so `isPending` covers the whole round trip.
export function JobAttachmentsSection({ businessId, jobId }: Props) {
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [caption, setCaption] = useState('');

  const attachmentsQuery = useQuery({
    queryKey: jobAttachmentsQueryKey(businessId, jobId),
    queryFn: () => listJobAttachments(businessId, jobId),
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const { url, fileType } = await uploadFile({
        businessId,
        file,
        kind: 'job_attachment',
        jobId,
      });
      return createJobAttachment(businessId, jobId, {
        file_type: fileType,
        file_url: url,
        caption: caption.trim() || undefined,
      });
    },
    meta: { successMessage: t.jobAttachmentAdded },
    onSuccess: () => {
      setCaption('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const attachments = attachmentsQuery.data ?? [];

  return (
    <div className="px-6 py-5 border-t flex flex-col gap-3">
      <h3 className="text-sm font-semibold">{t.jobAttachmentsHeading}</h3>

      {attachmentsQuery.isPending ? (
        <p className="text-sm text-muted-foreground">{t.loading}</p>
      ) : attachments.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.jobNoAttachments}</p>
      ) : (
        <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {attachments.map((att) => (
            <li key={att.id} data-testid={`job-attachment-${att.id}`}>
              <a
                href={att.file_url}
                target="_blank"
                rel="noreferrer"
                className="block border rounded-lg overflow-hidden hover:shadow-sm transition-shadow"
              >
                {att.file_type === 'image' ? (
                  <img
                    src={att.file_url}
                    alt={att.caption ?? ''}
                    className="w-full h-24 object-cover"
                  />
                ) : (
                  <div className="w-full h-24 flex items-center justify-center bg-muted text-muted-foreground">
                    <FileTextIcon className="w-6 h-6" />
                  </div>
                )}
                <p className="text-[11px] truncate px-1.5 py-1">
                  {att.caption || t.jobAttachmentOpen}
                </p>
              </a>
              <p className="text-[10px] text-muted-foreground px-1.5">
                {att.uploaded_by_full_name} ·{' '}
                {formatDate(att.created_at.slice(0, 10))}
              </p>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-2 pt-1">
        <Input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder={t.jobAttachmentCaptionPlaceholder}
          aria-label={t.jobAttachmentCaption}
          data-testid="job-attachment-caption"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_ATTACHMENT_TYPES.join(',')}
          data-testid="job-attachment-file"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) uploadMutation.mutate(file);
          }}
        />
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="self-start gap-1.5"
          disabled={uploadMutation.isPending}
          data-testid="job-attachment-upload"
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadIcon className="w-3.5 h-3.5" />
          {uploadMutation.isPending
            ? t.jobAttachmentUploading
            : t.jobAddAttachment}
        </Button>
      </div>
    </div>
  );
}
